import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Package, ShoppingBag, AlertCircle, Check } from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import { fetchOrderById } from '../../utils/api';
import { formatCurrency, formatDateTime } from '../../utils/formatters';
import {
  getOrderStatusLabel,
  getOrderStatusBadgeClass,
  getPaymentMethodLabel,
  getPaymentStatusLabel,
  isValidOrderNo,
  ORDER_TIMELINE,
  ORDER_STATUS,
} from '../../utils/constants';

const OrderSearchPage = () => {
  const [searchParams] = useSearchParams();

  const [orderId, setOrderId] = useState('');
  const [verifyName, setVerifyName] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  // 주문 조회 로직 (orderId, 선택적 senderName 검증)
  const runSearch = useCallback(async (rawId, name = '') => {
    const id = rawId.trim();

    if (!id) {
      setError('주문번호를 입력해주세요.');
      return;
    }
    // B3: 주문번호 형식 검증 (ORD-YYYYMMDD-NNN)
    if (!isValidOrderNo(id)) {
      setError('주문번호 형식이 올바르지 않습니다. (예: ORD-20250116-001)');
      setOrder(null);
      setSearched(true);
      return;
    }

    setLoading(true);
    setError('');
    setSearched(false);

    try {
      const foundOrder = await fetchOrderById(id);

      if (!foundOrder) {
        setOrder(null);
        setError('주문을 찾을 수 없습니다. 주문번호를 확인해주세요.');
      } else if (name.trim() && foundOrder.senderName !== name.trim()) {
        // 복합 인증: 보내는 분 이름이 입력된 경우 일치해야 조회 허용
        setOrder(null);
        setError('주문번호와 보내는 분 성함이 일치하지 않습니다.');
      } else {
        setOrder(foundOrder);
        setError('');
      }
    } catch (err) {
      setOrder(null);
      setError('주문 조회 중 오류가 발생했습니다.');
      console.error('Order search error:', err);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  }, []);

  // 쿼리 파라미터(?orderId=) 로 진입 시 자동 조회 (OrderCompletePage CTA 연동)
  useEffect(() => {
    const qId = searchParams.get('orderId');
    if (qId) {
      setOrderId(qId);
      runSearch(qId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, runSearch]);

  const handleSearch = (e) => {
    e.preventDefault();
    runSearch(orderId, verifyName);
  };

  const handleReset = () => {
    setOrderId('');
    setVerifyName('');
    setOrder(null);
    setError('');
    setSearched(false);
  };

  // 타임라인 렌더링 데이터 계산
  const renderTimeline = () => {
    if (!order) return null;
    const status = order.orderStatus;

    if (status === 'cancelled') {
      return (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          이 주문은 취소되었습니다.
        </div>
      );
    }

    const currentStep = ORDER_STATUS[status]?.step ?? 0;

    return (
      <ol className="relative flex justify-between">
        {ORDER_TIMELINE.map((stepKey, idx) => {
          const stepMeta = ORDER_STATUS[stepKey];
          const done = stepMeta.step <= currentStep;
          const isLast = idx === ORDER_TIMELINE.length - 1;

          // 단계별 시각 표시
          let timeLabel = '';
          if (stepKey === 'pending' && order.orderedAt) {
            timeLabel = formatDateTime(order.orderedAt);
          } else if (stepKey === 'delivered' && order.deliveredAt) {
            timeLabel = formatDateTime(order.deliveredAt);
          }

          return (
            <li key={stepKey} className="flex-1 flex flex-col items-center text-center relative">
              {/* 연결선 */}
              {!isLast && (
                <span
                  className={`absolute top-4 left-1/2 w-full h-0.5 ${
                    stepMeta.step < currentStep ? 'bg-primary' : 'bg-accent-200'
                  }`}
                  aria-hidden="true"
                />
              )}
              <span
                className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 ${
                  done
                    ? 'bg-primary border-primary text-white'
                    : 'bg-white border-accent-300 text-accent-400'
                }`}
              >
                {done ? <Check size={16} /> : idx + 1}
              </span>
              <span
                className={`mt-2 text-xs font-medium ${
                  done ? 'text-primary' : 'text-accent-400'
                }`}
              >
                {stepMeta.label}
              </span>
              {timeLabel && (
                <span className="mt-0.5 text-[10px] text-accent-500">{timeLabel}</span>
              )}
            </li>
          );
        })}
      </ol>
    );
  };

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="max-w-2xl mx-auto">
          {/* 페이지 헤더 */}
          <div className="mb-6 md:mb-8 text-center">
            <h1 className="text-2xl md:text-3xl font-bold text-primary mb-2">주문 조회</h1>
            <p className="text-accent-600">주문번호를 입력하여 주문 내역을 확인하세요</p>
          </div>

          {/* 검색 폼 */}
          <Card padding="lg" shadow="default" className="mb-6">
            <form onSubmit={handleSearch}>
              <div className="space-y-4">
                <Input
                  label="주문번호"
                  name="orderId"
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  placeholder="예: ORD-20250116-001"
                  error={error && !order ? error : ''}
                />
                <Input
                  label="보내는 분 성함 (선택 - 본인 확인용)"
                  name="verifyName"
                  value={verifyName}
                  onChange={(e) => setVerifyName(e.target.value)}
                  placeholder="주문 시 입력한 성함"
                />

                <div className="flex gap-3">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    disabled={loading}
                    className="flex items-center justify-center gap-2"
                  >
                    <Search size={20} />
                    {loading ? '조회 중...' : '조회하기'}
                  </Button>

                  {searched && (
                    <Button type="button" variant="outline" size="lg" onClick={handleReset}>
                      초기화
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </Card>

          {/* 주문을 찾을 수 없음 */}
          {searched && !order && !loading && (
            <div className="flex items-center justify-center py-10">
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                  <AlertCircle size={40} className="text-red-500" />
                </div>
                <h2 className="text-xl font-bold text-primary mb-2">주문을 찾을 수 없습니다</h2>
                <p className="text-accent-600 mb-4">{error || '주문번호를 다시 확인해주세요'}</p>
                <Button variant="outline" onClick={handleReset}>
                  다시 검색
                </Button>
              </div>
            </div>
          )}

          {/* 주문 정보 표시 */}
          {order && (
            <div className="space-y-4">
              {/* 주문 상태 + 타임라인 */}
              <Card padding="lg">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-sm text-accent-600 mb-1">주문번호</p>
                    <p className="text-xl font-bold text-primary">{order.id}</p>
                  </div>
                  <div className="text-right">
                    <span
                      className={`inline-block px-4 py-2 rounded-full font-medium ${getOrderStatusBadgeClass(
                        order.orderStatus
                      )}`}
                    >
                      {getOrderStatusLabel(order.orderStatus)}
                    </span>
                  </div>
                </div>

                {/* 배송 추적 타임라인 */}
                <div className="mb-4">{renderTimeline()}</div>

                <div className="text-sm text-accent-600 pt-4 border-t border-accent-200">
                  주문일시: {order.orderedAt ? formatDateTime(order.orderedAt) : '-'}
                  {order.deliveredAt && (
                    <span className="ml-4">
                      배송완료: {formatDateTime(order.deliveredAt)}
                    </span>
                  )}
                </div>
              </Card>

              {/* 주문 상품 */}
              <Card padding="lg">
                <h3 className="font-bold text-primary mb-4 flex items-center gap-2">
                  <ShoppingBag size={20} />
                  주문 상품
                </h3>
                <div className="space-y-3">
                  {order.items.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-4 pb-3 border-b border-accent-200 last:border-0"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-primary">{item.wreathName}</p>
                        <p className="text-sm text-accent-600">
                          {item.quantity}개 × {formatCurrency(item.price)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-primary">
                          {formatCurrency(item.price * item.quantity)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-accent-200">
                  <div className="flex items-center justify-between text-lg">
                    <span className="font-bold text-primary">총 결제 금액</span>
                    <span className="text-2xl font-bold text-primary">
                      {formatCurrency(order.totalAmount)}
                    </span>
                  </div>
                </div>
              </Card>

              {/* 배송지 정보 */}
              <Card padding="lg">
                <h3 className="font-bold text-primary mb-4 flex items-center gap-2">
                  <Package size={20} />
                  배송지 정보
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-accent-600">보내는 분</span>
                    <span className="font-medium text-primary">{order.senderName}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-accent-600">연락처</span>
                    <span className="font-medium text-primary">{order.deliveryPhone}</span>
                  </div>
                  <div className="flex items-start justify-between">
                    <span className="text-accent-600">배송 주소</span>
                    <span className="font-medium text-primary text-right max-w-[60%]">
                      {order.deliveryAddress}
                    </span>
                  </div>
                  {order.messageCard && (
                    <div className="mt-3 p-3 bg-accent-50 rounded-lg">
                      <p className="text-xs text-accent-600 mb-1">메시지 카드</p>
                      <p className="text-sm text-primary whitespace-pre-line">
                        {order.messageCard}
                      </p>
                    </div>
                  )}
                </div>
              </Card>

              {/* 결제 정보 */}
              <Card padding="lg">
                <h3 className="font-bold text-primary mb-4">결제 정보</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-accent-600">결제 방법</span>
                    <span className="font-medium text-primary">
                      {getPaymentMethodLabel(order.paymentMethod)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-accent-600">결제 상태</span>
                    <span className="font-medium text-green-600">
                      {getPaymentStatusLabel(order.paymentStatus)}
                    </span>
                  </div>
                </div>
              </Card>

              {/* 안내 */}
              <div className="bg-accent-50 rounded-lg border border-accent-200 p-4">
                <p className="text-xs text-accent-700">
                  문의사항이 있으시면 고객센터(1234-5678)로 연락주세요.
                </p>
              </div>
            </div>
          )}

          {/* 도움말 */}
          {!searched && !order && (
            <Card padding="lg" className="bg-accent-50 border-accent-200">
              <h3 className="font-bold text-primary mb-3 text-sm">주문 조회 안내</h3>
              <ul className="text-xs text-accent-700 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1"></span>
                  <span>주문 완료 시 받으신 주문번호를 입력하세요</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1"></span>
                  <span>주문번호 형식은 ORD-YYYYMMDD-순번 입니다 (예: ORD-20250116-001)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1"></span>
                  <span>보내는 분 성함을 함께 입력하면 본인 확인 후 조회됩니다</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1"></span>
                  <span>회원 가입 후 마이페이지에서도 주문 내역을 확인할 수 있습니다</span>
                </li>
              </ul>
            </Card>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default OrderSearchPage;
